import {
  getViewName,
  hideDashWindow,
  launchPersistentWindow,
  sendMessageToRenderer,
} from "@mirage/service-fss-native";
import {
  PERSISTENT_WINDOW_VIEW_NAME,
  ROUTE_PATHS,
} from "@mirage/service-fss-native/types";
import { tagged } from "@mirage/service-logging";
import { isEsvConfig } from "@mirage/shared/util/esv";
import { useLayoutEffect } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

const logger = tagged("esv-navigation-interceptor");

/**
 * A hook that listens for navigation changes in the ESV launcher window
 * and sends them to the persistent window via sendMessageToRenderer
 *
 * @returns void
 */
export function useEsvNavigationInterceptor(): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const handleNavigation = async () => {
      // Only intercept if we're in ESV mode
      if (!isEsvConfig()) {
        return;
      }

      // Don't intercept when navigationType is POP (back/forward navigation)
      // or when the navigation is meant to stay in launcher
      if (navigationType === "POP" || location.state?.doNotIntercept) {
        logger.debug(
          `Skipping navigation intercept: doNotIntercept=${location.state?.doNotIntercept}, navigationType=${navigationType}, route=${location.pathname}`,
        );
        return;
      }

      // Don't intercept navigation to the root page
      if (
        location.pathname === ROUTE_PATHS.ROOT ||
        location.pathname === ROUTE_PATHS.EMBED_LAUNCHER
      ) {
        logger.debug(
          `Skipping navigation intercept: root page or launcher page`,
        );
        return;
      }

      if (
        location.pathname === ROUTE_PATHS.CHAT &&
        !location.search &&
        !location.state
      ) {
        logger.debug(
          `Skipping navigation intercept: chat page with no search params or state`,
        );
        return;
      }

      // Don't intercept if we're in the persistent window
      try {
        const viewName = await getViewName();
        if (viewName === PERSISTENT_WINDOW_VIEW_NAME) {
          return;
        }
      } catch (error) {
        logger.error(
          "Error getting view name, continuing with navigation interception",
          error,
        );
      }

      logger.debug(
        `Intercepting navigation in ESV launcher and sending message to persistent window: ${location.pathname} : ${location.search} : ${location.hash}`,
      );

      // Launch persistent window and send navigation message
      try {
        await launchPersistentWindow();
      } catch (error) {
        logger.error("Error launching persistent window", error);
        return; // Exit early if we can't launch the persistent window
      }

      // Send navigation message to persistent window and hide the launcher
      Promise.all([
        sendMessageToRenderer({
          sender: "fss-launcher",
          recipient: "fss-persistent-window",
          actionType: "navigate",
          payload: {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            state: location.state,
          },
        }),
        hideDashWindow(),
        navigate(`${ROUTE_PATHS.EMBED_LAUNCHER}?resetTypeahead=true`, {
          state: {
            doNotIntercept: true,
          },
        }),
      ]).catch((err) => {
        logger.error(
          "Error sending navigation message to persistent window or hiding launcher",
          err,
        );
      });
    };

    handleNavigation();
  }, [location, navigationType, navigate]);
}
